import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { LandingPage } from '../components/landing';
import { useProjectStore } from '../stores/projectStore';
import { useAuth, useAuthActions } from '../context/AuthContext';
import { useSessionActions } from '../context/SessionContext';
import { useShellUiActions } from '../context/ShellUiContext';
import { MainView, ProjectType, ROUTE_PATTERNS, landingRedirectsToConnect } from '../router/routes';
import { isMcpOnlyCustomer } from '../utils/moduleAccess';

// BAR-40 S4: /landing route (the app's home). Replaces the mainView==='landing'
// ternary branch verbatim. onHomeAction's create-then-seed ordering mirrors
// NewProjectForm.onCreated (resetChatState → prefill+autoSend → navigate →
// setWorkspaceMode) so GeminiChatArea sees the seed + auto-send on its mount
// render; prefill setters come from ShellUi context (same state the chat reads).
export default function LandingRoute() {
  const { userData, authToken, connectPending, signupToken, resetToken, inviteToken } = useAuth();
  const { isModuleDisabled } = useAuthActions();
  const { resetChatState } = useSessionActions();
  const { setPrefillChatInput, setAutoSendPrefill, handleSelectProject, navigateToView } = useShellUiActions();
  const setWorkspaceMode = useProjectStore((s) => s.setWorkspaceMode);
  const { search } = useLocation();

  // A free (MCP-only) account has every door on Home locked, so its home is
  // the connect page. `replace`: Back must not land on `/` and bounce again.
  if (landingRedirectsToConnect({
    mcpOnly: isMcpOnlyCustomer(isModuleDisabled),
    connectPending,
    signupToken,
    resetToken,
    inviteToken,
    search,
  })) {
    return <Navigate to={ROUTE_PATTERNS.connect} replace />;
  }

  return (
    <LandingPage
      userName={userData?.name || 'User'}
      userId={userData?.email || ''}
      authToken={authToken || undefined}
      onNavigate={(destination: MainView, projectType?: ProjectType) => {
        // BAR-40: one navigation carries the new-project track in
        // the URL; navigating away from /quick-research drops its
        // ?q prefill automatically (no manual clear needed).
        navigateToView(destination, {
          newProjectType: destination === 'new-project' ? projectType : undefined,
        });
      }}
      onProjectSelect={handleSelectProject}
      onHomeAction={async (action: string, query?: string, history?: Array<{ role: 'user' | 'assistant'; content: string }>) => {
        // BAR-82: stop disabled-module actions before they navigate.
        // The home-agent backend filters these out for restricted
        // companies, but if a stale suggestion sneaks through we still
        // refuse to act on it.
        if (isModuleDisabled(action)) {
          return;
        }
        if (action === 'quick_lookup') {
          navigateToView('quick-research', { quickQuery: query });
          return;
        }
        if (action === 'project_library') { navigateToView('projects'); return; }
        // Anything else that is not a project action is ignored, never read
        // as "create a research project": a stale suggestion from an older
        // backend (the retired `report_library`) must not create one.
        if (action !== 'regulatory_research' && action !== 'build_artifact') {
          console.warn(`[LandingRoute] unknown home action ignored: ${action}`);
          return;
        }

        const projectType: 'build' | 'research' = action === 'build_artifact' ? 'build' : 'research';
        const userId = userData?.email || userData?.uid;
        // BAR-40: never create an owner-less project — if the user object hasn't
        // hydrated yet, refuse rather than create with an empty owner id.
        if (!userId) {
          console.warn('[LandingRoute] home action ignored: user not loaded yet.');
          return;
        }
        const targetView = projectType === 'build' ? 'build-artifacts' : 'workspace';
        const projectName = (query || 'New project').slice(0, 80);

        try {
          const result = await useProjectStore.getState().createProject(
            userId,
            projectName,
            query || '',
            projectType,
          );
          if (result?.project_id) {
            // BAR-320: replay the home-page discussion onto the new project
            // BEFORE resetChatState/navigate. createProject has just set this
            // project active, so seedConversationHistory's addLocalEvent calls
            // land in firestoreEvents synchronously — the chat renders the
            // prior turns. The model sees them only once they are persisted
            // (the orchestrator loads history from the event log, BAR-1551;
            // see seedConversationHistory). (No event reload happens here: App's
            // hydration guard short-circuits because the project is already
            // active, so seeding into the store is what makes it visible.)
            if (history && history.length > 0) {
              useProjectStore.getState().seedConversationHistory(result.project_id, userId, history);
            }
            // Smart project title (BAR-320 follow-up): rename from the topic of
            // the discussion instead of the truncated raw query. Fire-and-forget
            // — the project shows its placeholder name until this resolves.
            void useProjectStore.getState().autoTitleProject(
              result.project_id, userId, query || '', projectName, history || [],
            );
            // Same ordering as NewProjectForm onCreated:
            // resetChatState → prefill+autoSend → navigate,
            // so GeminiChatArea sees the seed and the
            // auto-send opt-in in its mount render.
            resetChatState(result.project_id);
            const seed = query?.trim();
            if (seed) {
              setPrefillChatInput(seed);
              setAutoSendPrefill(true);
            }
            navigateToView(targetView, { projectId: result.project_id });
            setWorkspaceMode(projectType === 'build' ? 'plan' : 'research');
            return;
          }
        } catch (err) {
          console.error('Failed to create project from home action:', err);
          throw err;
        }

        throw new Error('Failed to create project');
      }}
    />
  );
}
